import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { AUTH_STALE_TIME, authQueryKeys } from '../auth/queryKeys';
import type { Benchmarks } from '../../data/analytics';
import {
  fetchAccountUsage,
  fetchBenchmarks,
  fetchNetworkUsage,
} from './repository';

export const NETWORK_STALE_TIME = 60 * 1_000;

const accountUsageQueryKeys = {
  all: (subject: string) => [...authQueryKeys.subject(subject), 'usage'] as const,
  period: (subject: string, days: number, timeZone: string) =>
    [...accountUsageQueryKeys.all(subject), days, timeZone] as const,
};

const usageQueryKeys = {
  all: ['usage'] as const,
  network: () => [...usageQueryKeys.all, 'network'] as const,
  benchmarks: () => [...usageQueryKeys.all, 'benchmarks'] as const,
};

export const networkUsageQueryOptions = () =>
  queryOptions({
    queryKey: usageQueryKeys.network(),
    queryFn: fetchNetworkUsage,
    staleTime: NETWORK_STALE_TIME,
  });

export const benchmarksQueryOptions = () =>
  queryOptions<Benchmarks>({
    queryKey: usageQueryKeys.benchmarks(),
    queryFn: fetchBenchmarks,
    staleTime: NETWORK_STALE_TIME,
  });

/**
 * Route-loader resolution of the measured figures, so a page that shows them
 * server-renders them. Never rejects: these are an enhancement to a page whose
 * substance is the catalog, and dropping the route to an error boundary over a
 * metrics hiccup would replace a complete page with a full-page error. The
 * consumers already render a dash for an absent figure.
 */
export const loadMeasuredFigures = (queryClient: QueryClient): Promise<unknown> =>
  Promise.all([
    queryClient.ensureQueryData(benchmarksQueryOptions()).catch(() => undefined),
    queryClient.ensureQueryData(networkUsageQueryOptions()).catch(() => undefined),
  ]);

/** The zone is part of the key because it is part of the answer: the same
 *  window bucketed in two zones is two different series. */
export const accountUsageQueryOptions = (subject: string, days: number, timeZone: string) =>
  queryOptions({
    queryKey: accountUsageQueryKeys.period(subject, days, timeZone),
    queryFn: () => fetchAccountUsage(days, timeZone),
    staleTime: AUTH_STALE_TIME,
  });
